import http from './http';
import moment from './date';

export function toIsoDate(value) {
  return moment(value).format('YYYY-MM-DD');
}

export function addPeriod(iso, range, delta) {
  const date = moment(iso);
  if (range === 'week') return date.add(delta, 'weeks').format('YYYY-MM-DD');
  if (range === 'month') return date.add(delta, 'months').format('YYYY-MM-DD');
  return date.add(delta, 'days').format('YYYY-MM-DD');
}

export function periodLabel(iso, range) {
  const date = moment(iso);
  if (range === 'week') {
    const start = date.clone().startOf('isoWeek');
    const end = date.clone().endOf('isoWeek');
    return `${start.format('DD/MM')} – ${end.format('DD/MM/YYYY')}`;
  }
  if (range === 'month') {
    return date.format('MMMM [de] YYYY');
  }
  return date.format('dddd, DD [de] MMMM [de] YYYY');
}

export function isAvailabilitySlot(schedule) {
  return Boolean(schedule?.extendedProps?.isAvailabilitySlot || schedule?.display === 'background');
}

export const BLOCKED_SCHEDULE_COLOR = '#a020f0';

export function isBlockedSchedule(schedule) {
  if (!schedule) return false;
  return !schedule.id_sched || schedule.sched_status === 'BL';
}

export function blockedTimeRangeLabel(schedule, stepMinutes = 10) {
  const start = moment(schedule?.schedule_date_time || schedule?.sched_date);
  if (!start.isValid()) return schedule?.sched_time || '';
  const end = blockedEndMoment(schedule, stepMinutes);
  const startLabel = schedule?.sched_time || start.format('HH:mm');
  const endLabel = end.format('HH:mm');
  return startLabel === endLabel ? startLabel : `${startLabel} – ${endLabel}`;
}

export function mergeAdjacentBlockedSchedules(schedules, stepMinutes = 10) {
  const step = Number(stepMinutes) > 0 ? Number(stepMinutes) : 10;
  const others = [];
  const blocked = [];
  (schedules || []).forEach((item) => {
    (isBlockedSchedule(item) ? blocked : others).push(item);
  });
  blocked.sort((a, b) => moment(a.schedule_date_time).diff(moment(b.schedule_date_time)));

  const groups = [];
  blocked.forEach((item) => {
    const start = moment(item.schedule_date_time);
    const end = blockedEndMoment(item, step);
    const prev = groups[groups.length - 1];
    const canMerge = prev
      && toIsoDate(prev.item.schedule_date_time) === toIsoDate(item.schedule_date_time)
      && String(prev.item.id_prof || '') === String(item.id_prof || '')
      && String(prev.item.patient_name || '') === String(item.patient_name || '')
      && prev.end.isSameOrAfter(start);

    if (canMerge) {
      if (end.isAfter(prev.end)) {
        prev.end = end;
        prev.item = {
          ...prev.item,
          interval_minutes: end.diff(moment(prev.item.schedule_date_time), 'minutes'),
          id_avail_blocks: uniqueBlockIds(prev.item, item),
        };
      } else {
        prev.item = {
          ...prev.item,
          id_avail_blocks: uniqueBlockIds(prev.item, item),
        };
      }
      return;
    }

    groups.push({
      item: {
        ...item,
        interval_minutes: Math.max(end.diff(start, 'minutes'), step),
        id_avail_blocks: uniqueBlockIds(item),
      },
      end,
    });
  });

  return others
    .concat(groups.map((group) => group.item))
    .sort((a, b) => moment(a.schedule_date_time).diff(moment(b.schedule_date_time)));
}

export function uniqueBlockIds(...items) {
  const ids = [];
  items.forEach((item) => {
    (item?.id_avail_blocks || []).forEach((id) => ids.push(id));
    if (item?.id_avail_block) ids.push(item.id_avail_block);
  });
  return [...new Set(ids.filter((id) => id != null && id !== ''))];
}

export function isPatientSchedule(schedule) {
  return Boolean(schedule?.id_sched) && !isAvailabilitySlot(schedule) && schedule.sched_status !== 'BL';
}

export function compareByArrivalThenTime(a, b) {
  const arrive = moment(a?.sched_arrive).diff(moment(b?.sched_arrive));
  if (arrive) return arrive;
  const timeA = a?.schedule_date_time || a?.sched_date || a?.start;
  const timeB = b?.schedule_date_time || b?.sched_date || b?.start;
  return moment(timeA).diff(moment(timeB));
}

export function sortByArrivalThenTime(items) {
  return [...(items || [])].sort(compareByArrivalThenTime);
}

export function filterPatientSchedules(schedules, { idPaymeth, idInsurance } = {}) {
  if (!idPaymeth && !idInsurance) return schedules || [];
  return (schedules || []).filter((item) => {
    if (!isPatientSchedule(item)) return true;
    if (idPaymeth && String(item.id_paymeth) !== String(idPaymeth)) return false;
    if (idInsurance && String(item.id_insurance) !== String(idInsurance)) return false;
    return true;
  });
}

export function dayStats(schedules) {
  const items = (schedules || []).filter(isPatientSchedule);
  return {
    total: items.length,
    waiting: items.filter((item) => item.sched_arrive && !item.sched_start && item.sched_status !== 'FI').length,
    inCare: items.filter((item) => item.sched_start && item.sched_status !== 'FI').length,
    next: items.filter((item) => ['AG', 'CO'].includes(item.sched_status) && !item.sched_arrive).length,
  };
}

export function periodBounds(iso, range) {
  const date = moment(iso);
  if (range === 'week') {
    return {
      start: date.clone().startOf('isoWeek').format('YYYY-MM-DD'),
      end: date.clone().endOf('isoWeek').format('YYYY-MM-DD'),
    };
  }
  if (range === 'month') {
    return {
      start: date.clone().startOf('month').startOf('isoWeek').format('YYYY-MM-DD'),
      end: date.clone().endOf('month').endOf('isoWeek').format('YYYY-MM-DD'),
    };
  }
  return { start: date.format('YYYY-MM-DD'), end: date.format('YYYY-MM-DD') };
}

export function printBounds(iso, range) {
  const date = moment(iso);
  if (range === 'week') return periodBounds(iso, 'week');
  if (range === 'month') {
    return {
      start: date.clone().startOf('month').format('YYYY-MM-DD'),
      end: date.clone().endOf('month').format('YYYY-MM-DD'),
    };
  }
  return periodBounds(iso, 'day');
}

export function weekDays(iso) {
  const start = moment(iso).startOf('isoWeek');
  return Array.from({ length: 7 }, (_, index) => start.clone().add(index, 'days').format('YYYY-MM-DD'));
}

export function monthCells(iso) {
  const month = moment(iso).month();
  const start = moment(iso).startOf('month').startOf('isoWeek');
  const end = moment(iso).endOf('month').endOf('isoWeek');
  const cells = [];
  for (let cursor = start.clone(); cursor.isSameOrBefore(end, 'day'); cursor.add(1, 'day')) {
    cells.push({
      date: cursor.format('YYYY-MM-DD'),
      inMonth: cursor.month() === month,
    });
  }
  return cells;
}

export function weekdayShort(iso) {
  return moment(iso).format('ddd');
}

export function schedulesOnDate(schedules, iso) {
  const items = (schedules || [])
    .filter((item) => toIsoDate(item.schedule_date_time || item.sched_date || item.start) === iso)
    .sort((a, b) => moment(a.schedule_date_time || a.sched_date || a.start).diff(moment(b.schedule_date_time || b.sched_date || b.start)));
  return mergeAdjacentBlockedSchedules(items);
}

export function splitSchedulePayload(items) {
  const all = items || [];
  return {
    schedules: all.filter((item) => !isAvailabilitySlot(item)),
    availabilitySlots: all.filter(isAvailabilitySlot),
  };
}

export function loadNextScheduleDates(placeId, { idProf, idSpec, status, start, end }) {
  if (!placeId || !idProf || !start) {
    return Promise.resolve({ next_date: null, prev_date: null });
  }

  return http.get(`/places/${placeId}/schedules/next-date`, {
    params: {
      id_prof: idProf,
      id_spec: idSpec || null,
      sched_status: status || null,
      start_date: start,
      end_date: end || start,
      base: 'AL',
    },
  }).then((response) => ({
    next_date: response.data?.next_date ? toIsoDate(response.data.next_date) : null,
    prev_date: response.data?.prev_date ? toIsoDate(response.data.prev_date) : null,
  })).catch(() => ({ next_date: null, prev_date: null }));
}

export function loadPeriodSchedules(placeId, { idProf, idSpec, status, start, end }) {
  if (!placeId || !idProf || !start || !end) {
    return Promise.resolve({ schedules: [], availabilitySlots: [] });
  }

  return http.get(`/places/${placeId}/schedules`, {
    params: {
      id_prof: idProf,
      id_spec: idSpec || null,
      sched_status: status || null,
      start_date: start,
      end_date: end,
      base: 'AL',
    },
  }).then((response) => splitSchedulePayload(response.data));
}

export function loadAvailabilityDayRanges(placeId, { idProf, start, end }) {
  if (!placeId || !idProf || !start || !end) {
    return Promise.resolve([]);
  }

  return http.get(`/places/${placeId}/schedules/availability-day-ranges`, {
    params: {
      id_prof: idProf,
      start_date: start,
      end_date: end,
    },
  }).then((response) => response.data || []);
}

export function loadDaySchedules(placeId, { idProf, idSpec, status, date }) {
  return loadPeriodSchedules(placeId, {
    idProf,
    idSpec,
    status,
    start: date,
    end: date,
  });
}

export function resolveSlotStepMinutes(stepMinutes, availabilitySlots, date) {
  const configured = Number(stepMinutes);
  if (Number.isFinite(configured) && configured > 0) {
    return configured;
  }

  const durations = (availabilitySlots || [])
    .filter((slot) => slot?.start && slot?.end && toIsoDate(slot.start) === date)
    .map((slot) => moment(slot.end).diff(moment(slot.start), 'minutes'))
    .filter((minutes) => minutes > 0);

  if (durations.length) {
    const counts = durations.reduce((acc, minutes) => {
      acc[minutes] = (acc[minutes] || 0) + 1;
      return acc;
    }, {});
    return Number(Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0]);
  }

  return stepMinutes > 0 ? Number(stepMinutes) : 10;
}

export function buildDaySlots(schedules, {
  date,
  begin,
  end,
  stepMinutes,
  availabilitySlots = [],
  dayRange = null,
}) {
  const step = resolveSlotStepMinutes(stepMinutes, availabilitySlots, date);
  const { start, finish } = resolveDayWindow({
    date,
    begin,
    end,
    step,
    schedules,
    availabilitySlots,
    dayRange,
  });
  const items = mergeAdjacentBlockedSchedules(schedules, step);
  const today = moment().format('YYYY-MM-DD');
  const now = moment();

  const slots = [];
  for (let cursor = start.clone(); cursor.isBefore(finish); cursor.add(step, 'minutes')) {
    const covering = items.filter((item) => coversSlot(item, cursor, step));
    const starting = covering.filter((item) => startsInSlot(item, cursor, step));
    const continuing = covering.filter((item) => !startsInSlot(item, cursor, step));
    const available = isWorkingSlot(cursor, dayRange);
    const empty = covering.length === 0;
    const slotEnd = cursor.clone().add(step, 'minutes');
    const past = empty && date === today && !slotEnd.isAfter(now);
    slots.push({
      time: cursor.format('HH:mm'),
      datetime: cursor.toDate(),
      starting,
      continuing,
      occupied: covering.length > 0,
      past,
      free: empty && available && !past,
      fitted: empty && !available && !past,
    });
  }
  return slots;
}

function durationMinutes(schedule, step) {
  const minutes = Number(schedule?.interval_minutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : Number(step) || 0;
}

export function scheduleDurationMinutes(schedule, step) {
  return durationMinutes(schedule, step);
}

export const TIMELINE_PX_PER_MINUTE = 3.5;

export function timelineSlotHeight(stepMinutes) {
  const step = Number(stepMinutes) > 0 ? Number(stepMinutes) : 10;
  return Math.max(40, step * TIMELINE_PX_PER_MINUTE);
}

export const TIMELINE_EMPTY_SLOT_PX = 38;
export const TIMELINE_FITTED_GROUP_PX = 42;
export const TIMELINE_FITTED_COLLAPSE_MIN = 3;

export function fittedSlotGroups(slots, minCount = TIMELINE_FITTED_COLLAPSE_MIN) {
  const groups = [];
  let start = -1;
  (slots || []).forEach((slot, index) => {
    if (slot?.fitted) {
      if (start < 0) start = index;
      return;
    }
    if (start >= 0 && index - start >= minCount) {
      groups.push({ start, end: index - 1 });
    }
    start = -1;
  });
  if (start >= 0 && slots.length - start >= minCount) {
    groups.push({ start, end: slots.length - 1 });
  }
  return groups;
}

export function timelineOffsetAt(time, { dayStart, step, slotHeights, slotPx }) {
  const origin = moment(dayStart);
  const minutes = moment(time).diff(origin, 'minutes', true);
  if (!slotHeights || !slotHeights.length) {
    return (minutes / step) * slotPx;
  }
  if (minutes <= 0) return 0;
  const index = Math.floor(minutes / step);
  const frac = (minutes / step) - index;
  if (index >= slotHeights.length) {
    return slotHeights.reduce((sum, height) => sum + height, 0);
  }
  let offset = 0;
  for (let i = 0; i < index; i += 1) offset += slotHeights[i];
  return offset + frac * slotHeights[index];
}

function rangesOverlap(left, right) {
  return left.endMinutes > right.startMinutes && right.endMinutes > left.startMinutes;
}

function assignOverlapColumns(items) {
  const placed = [];
  items.forEach((item) => {
    const used = new Set();
    placed.forEach((other) => {
      if (rangesOverlap(item, other)) used.add(other.column);
    });
    let column = 0;
    while (used.has(column)) column += 1;
    item.column = column;
    placed.push(item);
  });

  const clusters = [];
  items.forEach((item) => {
    const hits = [];
    clusters.forEach((group, index) => {
      if (group.some((other) => rangesOverlap(item, other))) hits.push(index);
    });
    if (!hits.length) {
      clusters.push([item]);
      return;
    }
    const merged = [item];
    hits.reverse().forEach((index) => {
      merged.push(...clusters[index]);
      clusters.splice(index, 1);
    });
    clusters.push(merged);
  });
  clusters.forEach((group) => {
    const columns = Math.max(...group.map((item) => item.column)) + 1;
    group.forEach((item) => {
      item.columns = columns;
    });
  });
}

export function layoutDayTimelineEvents(schedules, { dayStart, step, slotPx, slotHeights }) {
  const scale = { dayStart, step, slotPx, slotHeights };
  const origin = moment(dayStart);
  const items = (schedules || [])
    .filter(isPatientSchedule)
    .map((schedule) => {
      const start = moment(schedule.schedule_date_time);
      const minutes = Math.max(durationMinutes(schedule, step), 1);
      const startMinutes = start.diff(origin, 'minutes', true);
      const top = timelineOffsetAt(start, scale);
      return {
        schedule,
        start,
        minutes,
        startMinutes,
        endMinutes: startMinutes + minutes,
        visualTop: top,
        column: 0,
        columns: 1,
      };
    })
    .sort((left, right) => left.start.diff(right.start) || right.minutes - left.minutes);

  assignOverlapColumns(items);

  return items.map((item) => {
    const durationEnd = item.start.clone().add(item.minutes, 'minutes');
    const height = Math.max(timelineOffsetAt(durationEnd, scale) - item.visualTop, 28);
    const width = 100 / item.columns;
    return {
      schedule: item.schedule,
      compact: false,
      style: {
        top: `${Math.max(item.visualTop, 0)}px`,
        height: `${height - 2}px`,
        left: `calc(${item.column * width}% + 3px)`,
        width: `calc(${width}% - 6px)`,
      },
    };
  });
}

export function layoutDayTimelineBlocks(schedules, { dayStart, step, slotPx, slotHeights }) {
  const scale = { dayStart, step, slotPx, slotHeights };
  return mergeAdjacentBlockedSchedules(schedules, step)
    .filter(isBlockedSchedule)
    .map((item) => {
      const start = moment(item.schedule_date_time);
      const end = blockedEndMoment(item, step).add(Number(step) || 0, 'minutes');
      const top = timelineOffsetAt(start, scale);
      const height = timelineOffsetAt(end, scale) - top;
      return {
        item,
        label: item.patient_name || '',
        timeRange: blockedTimeRangeLabel(item, step),
        blockIds: uniqueBlockIds(item),
        style: {
          top: `${Math.max(top, 0)}px`,
          height: `${Math.max(height, 28)}px`,
          left: '3px',
          width: 'calc(100% - 6px)',
        },
      };
    });
}

function blockDurationMinutes(schedule) {
  const minutes = Number(schedule?.interval_minutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

function startsInSlot(schedule, cursor, step) {
  const start = moment(schedule.schedule_date_time);
  const slotEnd = cursor.clone().add(step, 'minutes');
  return start.isSameOrAfter(cursor) && start.isBefore(slotEnd);
}

function coversSlot(schedule, cursor, step) {
  if (startsInSlot(schedule, cursor, step)) return true;

  const start = moment(schedule.schedule_date_time);
  const slotEnd = cursor.clone().add(step, 'minutes');

  if (isBlockedSchedule(schedule)) {
    const end = blockedEndMoment(schedule, step);
    return start.isBefore(slotEnd) && end.isSameOrAfter(cursor, 'minute');
  }

  const minutes = durationMinutes(schedule, step);
  if (minutes <= step) return false;

  const end = start.clone().add(minutes, 'minutes');
  return start.isBefore(slotEnd) && end.isAfter(cursor);
}

function isWorkingSlot(cursor, dayRange) {
  const ranges = dayRange?.ranges;
  if (!Array.isArray(ranges) || !ranges.length) return false;

  const time = cursor.format('HH:mm:ss');
  return ranges.some((range) => {
    const start = normalizeTime(range.start_time);
    const end = normalizeTime(range.end_time);
    return time >= start && time < end;
  });
}

function resolveDayWindow({ date, begin, end, step, schedules, availabilitySlots, dayRange }) {
  const daySlots = (availabilitySlots || []).filter((slot) => toIsoDate(slot.start) === date && slot.start && slot.end);
  let minTime = dayRange?.min_time || null;
  let maxTime = dayRange?.max_time || null;
  const fromAvailability = Boolean(minTime && maxTime);

  if (!minTime || !maxTime) {
    const times = daySlots.flatMap((slot) => [
      moment(slot.start).format('HH:mm:ss'),
      moment(slot.end).format('HH:mm:ss'),
    ]);
    if (times.length) {
      minTime = minTime || times.reduce((left, right) => (left < right ? left : right));
      maxTime = maxTime || times.reduce((left, right) => (left > right ? left : right));
    }
  }

  const fromSlots = Boolean(!fromAvailability && daySlots.length);
  minTime = minTime || begin || '08:00:00';
  maxTime = maxTime || end || '18:00:00';

  if (!fromAvailability && !fromSlots) {
    maxTime = addInterval(maxTime, step);
  }

  (schedules || []).forEach((item) => {
    if (!item.schedule_date_time) return;
    const start = moment(item.schedule_date_time).format('HH:mm:ss');
    const finishAt = occupancyEnd(item, step);
    const finish = finishAt.format('HH:mm:ss');
    if (toIsoDate(item.schedule_date_time) === date && start < minTime) minTime = start;
    if (toIsoDate(finishAt) === date && finish > maxTime) maxTime = finish;
  });

  return {
    start: moment(`${date} ${normalizeTime(minTime)}`),
    finish: moment(`${date} ${normalizeTime(maxTime)}`),
  };
}

function occupancyEnd(schedule, step) {
  const start = moment(schedule.schedule_date_time);
  if (isBlockedSchedule(schedule)) {
    return blockedEndMoment(schedule, step).add(Number(step) || 0, 'minutes');
  }
  return start.clone().add(durationMinutes(schedule, step), 'minutes');
}

function blockedEndMoment(schedule, stepMinutes = 10) {
  const start = moment(schedule?.schedule_date_time);
  const minutes = blockDurationMinutes(schedule) || Number(stepMinutes) || 10;
  return start.clone().add(minutes, 'minutes');
}

function addInterval(time, minutes) {
  return moment(`2000-01-01 ${normalizeTime(time)}`).add(minutes || 0, 'minutes').format('HH:mm:ss');
}

export function currentSlotLabel(isoDate, stepMinutes) {
  if (isoDate !== moment().format('YYYY-MM-DD')) return '';
  const step = stepMinutes > 0 ? stepMinutes : 10;
  const now = moment();
  const minutes = Math.floor(now.minutes() / step) * step;
  return now.clone().startOf('hour').add(minutes, 'minutes').format('HH:mm');
}

function normalizeTime(value) {
  if (!value) return '00:00:00';
  const parts = String(value).split(':');
  return [
    (parts[0] || '00').padStart(2, '0'),
    (parts[1] || '00').padStart(2, '0'),
    (parts[2] || '00').padStart(2, '0'),
  ].join(':');
}
